import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { formatAdminRole } from "../accessControl";
import { callApi, getInlineLoader } from "../Helpers";
import {
  MdAdminPanelSettings,
  MdBadge,
  MdEdit,
  MdPhone,
  MdSecurity,
  MdVerifiedUser,
} from "react-icons/md";

export default function View({ id }) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  async function init() {
    setReady(false);
    await getData();
    setReady(true);
  }

  async function getData() {
    try {
      const response = await callApi("get-admin-data", { id });
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data");
      }
    } catch (err) {
      appContext.tellError("Connection error");
    }
  }

  useEffect(() => {
    init();
  }, [id]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => appContext.setShowOverlayLoader(false);
  }, [loading]);

  if (!ready || !data) {
    return <div className="container mSupportLoading">{getInlineLoader()}</div>;
  }

  return (
    <div className="container">
      <div className="detail-shell">
        <div className="detail-header">
          <div>
            <p className="app-page-eyebrow">Administrator profile</p>
            <h4 className="main-section-title mb-1">{data.fullname}</h4>
            <div className="d-flex align-items-center gap-3 flex-wrap text-muted small">
              <span>
                <MdPhone size={14} /> {data.phone || "No phone number"}
              </span>
              <span>
                <MdSecurity size={14} /> {formatAdminRole(data.role)}
              </span>
            </div>
          </div>

          <button
            className="btn btn-dark btn-rounded app-cta-btn"
            onClick={() =>
              appContext.navTo({
                item: "view",
                subItem: "create-admin",
                extraItem: data.id,
              })
            }
          >
            <MdEdit size={18} />
            Edit profile
          </button>
        </div>

        <div className="app-form-summary-grid mt-4">
          <div className="app-form-summary-card">
            <span>Role</span>
            <strong>{formatAdminRole(data.role)}</strong>
          </div>

          <div className="app-form-summary-card">
            <span>Phone</span>
            <strong>{data.phone || "Not set"}</strong>
          </div>
        </div>

        <div className="detail-actions-card mt-4">
          <h6 className="card-title mb-4">Account Information</h6>
          <div className="row">
            <div className="col-md-6 mb-4">
              <label className="form-label">Administrator Name</label>
              <h6 className="d-flex align-items-center gap-2 mb-0">
                <MdAdminPanelSettings size={16} />
                <span>{data.fullname || "Not set"}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-label">Assigned Role</label>
              <h6 className="d-flex align-items-center gap-2 mb-0">
                <MdBadge size={16} />
                <span>{formatAdminRole(data.role)}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-0">
              <label className="form-label">Contact Number</label>
              <h6 className="d-flex align-items-center gap-2 mb-0">
                <MdPhone size={16} />
                <span>{data.phone || "Not set"}</span>
              </h6>
            </div>

            <div className="col-md-6 mb-0">
              <label className="form-label">Email</label>
              <h6 className="d-flex align-items-center gap-2 mb-0">
                <MdPhone size={16} />
                <span>{data.email || "Not set"}</span>
              </h6>
            </div>
          </div>
        </div>

        <div className="detail-actions-card mt-4">
          <h6 className="card-title mb-4">Access Scope</h6>
          <div className="p-4 rounded-4 border" style={{ background: "rgba(1, 34, 70, 0.03)", borderColor: "rgba(1, 34, 70, 0.08)" }}>
            <div className="d-flex align-items-start gap-3">
              <div className="app-form-badge">
                <MdVerifiedUser size={18} />
                Access profile
              </div>
              <div>
                <h6 className="mb-2">{formatAdminRole(data.role)}</h6>
                <p className="text-muted small mb-0">
                  This account is configured for operational access based on the assigned role and can be updated anytime from the administrator management workspace.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
